package nl.volleyplanner.web.controller.members

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.dom.addClass
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.FlowContent
import kotlinx.html.InputType
import kotlinx.html.TagConsumer
import kotlinx.html.div
import kotlinx.html.dom.create
import kotlinx.html.fieldSet
import kotlinx.html.i
import kotlinx.html.id
import kotlinx.html.input
import kotlinx.html.label
import kotlinx.html.legend
import kotlinx.html.option
import kotlinx.html.role
import kotlinx.html.select
import kotlinx.html.tabIndex
import nl.volleyplanner.api.entities.Group
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.entities.OrganisationSeason
import nl.volleyplanner.api.entities.PersonUpdateRequest
import nl.volleyplanner.sportconfig.NevoboTeamName
import nl.volleyplanner.web.CoroutineManager.RENDER_SCOPE
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.BootstrapButton
import nl.volleyplanner.web.base.component.BootstrapButtonStyle
import nl.volleyplanner.web.base.component.ButtonVariant
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.ModalSize
import nl.volleyplanner.web.base.component.iconButton
import nl.volleyplanner.web.base.component.validatingForm
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.LedenController
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement

class TeamSelectionComponent(
    private val personRecordState: StateFlow<LedenController.PersonRecord?>
) : HTMLComponent() {

    var availableTeams: List<Group> = emptyList()

    private val selectedTeamsFlow = MutableStateFlow(emptySet<Group>())
    val selectedTeamsState = selectedTeamsFlow.asStateFlow()

    init {
        launchAsync(RENDER_SCOPE) {
            personRecordState.collect { personRecord ->
                selectedTeamsFlow.value = personRecord?.person?.groups?.get(GroupType.Team.paramValue).orEmpty().toSet()
            }
        }
    }

    private fun addTeam(team: Group) {
        selectedTeamsFlow.value = selectedTeamsFlow.value + team
    }

    private fun removeTeam(team: Group) {
        selectedTeamsFlow.value = selectedTeamsFlow.value - team
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "mb-4") {
            label(classes = "form-label") { +"Teams" }

            div(classes = "border rounded p-3") {
                val selectedTeams = selectedTeamsFlow.value.toList()

                // Render selected teams and the add-team card in a responsive grid: 1 per row on small, 2 per row on lg+
                div(classes = "row row-cols-1 row-cols-lg-2 g-3 mb-2") {
                    selectedTeams.forEach { team ->
                        div(classes = "col") {
                            div(classes = "card text-bg-light h-100 w-100") {
                                div(classes = "card-body d-flex justify-content-between align-items-center") {
                                    div(classes = "me-3") {
                                        div(classes = "fw-medium") { +team.name }
                                    }
                                    iconButton(BootstrapButtonStyle.Danger, "bi-dash-circle") { event ->
                                        event.preventDefault()
                                        removeTeam(team)
                                    }
                                    // Hidden input kept for legacy reasons, no longer used to build requests
                                    input(type = InputType.hidden) {
                                        name = "vp-selected-teams"
                                        value = team.id
                                    }
                                }
                            }
                        }
                    }

                    // Add-team card rendered like a regular card, same width
                    // TODO: Better way of comparing: compare by ID only
                    val availableToAdd = availableTeams.filter { it.id !in selectedTeams.map { team -> team.id} }
                    if (availableToAdd.isNotEmpty()) {
                        div(classes = "col") {
                            div(classes = "card h-100 w-100") {
                                div(classes = "card-body") {
                                    div(classes = "d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2") {
                                        select(classes = "form-select flex-grow-1") {
                                            id = "vp-new-team-select"
                                            option { value = ""; +"Selecteer team..." }
                                            availableToAdd
                                                .sortedBy { NevoboTeamName.parse(it.name).teamIdentifier }
                                                .forEach { group ->
                                                    option { value = group.id; +group.name }
                                                }
                                        }
                                        iconButton(BootstrapButtonStyle.Primary, "bi-plus-circle") { event ->
                                            event.preventDefault()
                                            val selectElement = document.getElementById("vp-new-team-select") as? org.w3c.dom.HTMLSelectElement
                                            val selectedValue = selectElement?.value
                                            if (!selectedValue.isNullOrBlank()) {
                                                val teamToAdd = availableTeams.find { it.id === selectedValue }
                                                if (teamToAdd != null) {
                                                    addTeam(teamToAdd)
                                                    // Reset select
                                                    selectElement.selectedIndex = 0
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } else if (selectedTeams.isEmpty()) {
                        // No teams selected and none available to add
                        div(classes = "col") {
                            div(classes = "text-muted fst-italic") { +"No teams assigned" }
                        }
                    }
                }
            }
            // Single hidden input mirroring the current selected team IDs for external readers
            input(type = InputType.hidden) {
                id = "vp-selected-team-ids"
                value = selectedTeamsState.value.joinToString(",") { it.id }
            }
        }
    }

    override suspend fun getState() = selectedTeamsFlow.asStateFlow()
}

class PersonEditModal(
    private val selectedSeasonFlow: StateFlow<OrganisationSeason?>,
    private val availableTeamAndRefereeGroups: StateFlow<Pair<List<Group>, List<Group>>?>,
    private val onPersonUpdate: () -> Unit = {}
) : ModalComponent(
    "Bewerk lid",
    ModalSize.Large
) {
    override val footerButtons = listOf(
        BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
        BootstrapButton("Opslaan", ButtonVariant.Primary, {
            it.preventDefault()

            // TODO: kill me now. Need to completely revamp the Modal component to properly submit forms through modal buttons
            //  super dirty workaround. Firing a submit event to trigger the form submit functions
            //  (cannot call submit directly as this would not use the custom submit functions and events)
            val formElement = document.getElementById("vp-person-edit-form-${personRecordState.value!!.person.id}")!! as HTMLFormElement
            formElement.dispatchEvent(org.w3c.dom.events.Event("submit"))
        }
        )
    )

    private val personRecordState = MutableStateFlow<LedenController.PersonRecord?>(null)

    private val teamSelectionComponent = TeamSelectionComponent(
        personRecordState = personRecordState
    )

    override suspend fun getState() = personRecordState.asStateFlow()
        .combineAndLaunch(RENDER_SCOPE, availableTeamAndRefereeGroups)
        .combineAndLaunch(RENDER_SCOPE, selectedSeasonFlow)

    fun showModal(personRecord: LedenController.PersonRecord) {
        personRecordState.value = personRecord
        teamSelectionComponent.availableTeams = availableTeamAndRefereeGroups.value?.first.orEmpty()
        super.show()
    }

    suspend fun save(event: kotlinx.html.org.w3c.dom.events.Event) {
        val personIdEl = document.getElementById("vp-person-relationnumber") as org.w3c.dom.HTMLInputElement
        val personNameEl = document.getElementById("vp-person-name") as org.w3c.dom.HTMLInputElement
        val refereeGroupEl = document.getElementById("vp-referee-group") as org.w3c.dom.HTMLSelectElement

        val teamIds = teamSelectionComponent.selectedTeamsState.value.map { it.id }

        val refereeValue = refereeGroupEl.value
        val refereeId = refereeValue.ifBlank { null }

        val personUpdateRequest = PersonUpdateRequest(
            id = personRecordState.value!!.person.id,
            relationNumber = personIdEl.value,
            name = personNameEl.value,
            seasonName = selectedSeasonFlow.value!!.name,
            teamGroupIds = teamIds,
            refereeGroupId = refereeId
        )

        VolleyPlannerApplication.volleyplannerApiClient
            .organisation
            .persons
            .updatePerson(personUpdateRequest)
    }

    override fun FlowContent.renderBody() {
        if (availableTeamAndRefereeGroups.value == null || personRecordState.value == null || selectedSeasonFlow.value == null) {
            spinner()
            return
        }

        val personRecord = personRecordState.value!!
        validatingForm(
            validSubmitFunction = { event, formElement ->
                event.preventDefault()
                val statusElement = document.getElementById("vp-person-edit-form-status")!! as HTMLElement

                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-secondary d-flex align-items-center") {
                    role = "alert"
                    spinner()
                    +"Gegevens opslaan..."
                })

                save(event)
                statusElement.addClass("d-none")
                hide()
                onPersonUpdate()
            },
            onSubmitErrorFunction = { throwable, formElement ->
                val statusElement = document.getElementById("vp-person-edit-form-status")!! as HTMLElement
                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-danger d-flex align-items-center") {
                    role = "alert"
                    icon("bi-exclamation-triangle")
                    +"Fout bij opslaan gegevens: ${throwable.message}"
                })
            }
        ) {
            id = "vp-person-edit-form-${personRecord.person.id}"

            div (classes = "d-none") {
                id = "vp-person-edit-form-status"
            }


            div(classes = "p-3 mb-2") {
                fieldSet {
                    // Person ID (readonly)
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-person-relationnumber"
                            +"Lidnummer"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-person-relationnumber"
                                name = "vp-person-relationnumber"
                                value = personRecord.person.relationNumber
                                readonly = true
                                tabIndex = "-1"
                            }
                            div(classes = "form-text") {
                                +"Neem contact op om dit te bewerken"
                            }
                        }
                    }

                    // Person Name
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-person-name"
                            +"Naam"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-person-name"
                                name = "vp-person-name"
                                value = personRecord.person.name
                                required = true
                            }
                            div(classes = "invalid-feedback") {
                                +"Een naam is verplicht."
                            }
                        }
                    }
                }
            }

            // Season Section - combining background, shadow, and fieldset
            div(classes = "bg-light-subtle border rounded p-3 mb-2") {
                fieldSet {
                    legend(classes = "h6 mb-1") {
                        +"Seizoen ${selectedSeasonFlow.value!!.name}"
                    }
                    div(classes = "text-muted small mb-4") {
                        +"De instellingen hieronder hebben enkel impact op dit seizoen"
                    }

                    // Referee Group Section
                    div (classes = "row g-3 mb-3 align-items-center") {
                        label(classes = "col-12 col-lg-4 col-form-label") {
                            htmlFor = "vp-referee-group"
                            +"Scheidsrechtersgroep"
                        }
                        div(classes = "col-12 col-lg-8 col-xl-6") {
                            select(classes = "form-select") {
                                id = "vp-referee-group"
                                name = "vp-referee-group"

                                option {
                                    value = ""
                                    +"Geen scheidsrechtersgroep"
                                }

                                val currentRefereeGroup = personRecord.refereeGroups.firstOrNull()
                                availableTeamAndRefereeGroups.value!!.second.forEach { group ->
                                    option {
                                        value = group.id
                                        if (currentRefereeGroup?.id == group.id) {
                                            selected = true
                                        }
                                        +group.name
                                    }
                                }
                            }
                        }
                    }

                    // Teams Section - using the component
                    div (classes = "row g-3 mb-3") {
                        div (classes = "col-12") {
                            component(teamSelectionComponent)
                        }
                    }

                    // Volunteer Jobs Section (unchanged content)
                    div (classes = "row g-3 mb-0") {
                        div(classes = "col-12") {
                            label(classes = "form-label") { +"Vrijwilligerstaken" }
                            div(classes = "border rounded p-3 bg-light") {
                                div(classes = "text-muted fst-italic text-center py-2") {
                                    i(classes = "bi bi-clock-history me-2")
                                    +"Het bewerken van vrijwilligerstaken wordt later toegevoegd"
                                }
                            }
                        }
                    }
                }
            }

        }
    }
}